package info.rezident.app.pages

import info.rezident.app.data.auth.AuthErrorHandler
import info.rezident.app.data.auth.AuthRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch
import kotlinx.serialization.Serializable

class AuthViewModel(
    private val onSuccessCallback: (() -> Unit)? = null
) {
    private val viewModelScope = CoroutineScope(Dispatchers.Default)

    private val _authUiState = MutableStateFlow<AuthUiState>(AuthUiState.Idle)
    val authUiState: StateFlow<AuthUiState> = _authUiState.asStateFlow()

    private val _navigationEffect = MutableSharedFlow<AuthNavigationEffect>()
    val navigationEffect: SharedFlow<AuthNavigationEffect> = _navigationEffect.asSharedFlow()

    fun signInWithEmail(email: String, password: String) {
        _authUiState.value = AuthUiState.Loading
        viewModelScope.launch {
            val result = AuthRepository.signInWithEmail(email, password)
            _authUiState.value = result.fold(
                onSuccess = {
                    handleAuthSuccess()
                    AuthUiState.Success
                },
                onFailure = { AuthUiState.Error(AuthErrorHandler.getErrorMessage(it)) }
            )
        }
    }

    fun registerWithEmail(email: String, password: String) {
        _authUiState.value = AuthUiState.Loading
        viewModelScope.launch {
            val result = AuthRepository.registerWithEmail(email, password)
            _authUiState.value = result.fold(
                onSuccess = {
                    handleAuthSuccess()
                    AuthUiState.Success
                },
                onFailure = { AuthUiState.Error(AuthErrorHandler.getErrorMessage(it)) }
            )
        }
    }

    fun resetState() {
        _authUiState.value = AuthUiState.Idle
    }

    private fun handleAuthSuccess() {
        onSuccessCallback?.invoke() ?: run {
            // Default behavior - emit navigation effect for Auth page
            viewModelScope.launch {
                _navigationEffect.emit(AuthNavigationEffect.NavigateToProfile)
            }
        }
    }
}

@Serializable
sealed class AuthUiState {
    @Serializable
    object Idle : AuthUiState()

    @Serializable
    object Loading : AuthUiState()

    @Serializable
    object Success : AuthUiState()

    @Serializable
    data class Error(val message: String) : AuthUiState()
}

sealed class AuthNavigationEffect {
    object NavigateToProfile : AuthNavigationEffect()
}